import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://10.0.0.27:8181";

export interface MarketingData {
  _id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  discountPercentage: number;
  couponId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingDataInput {
  title: string;
  subtitle: string;
  description: string;
  image?: string | File;
  discountPercentage: number;
  couponId: string;
  isActive: boolean;
}

export interface MarketingSubmission {
  firstName: string;
  email: string;
  phone: string;
  acceptMarketing: boolean;
  marketingDataId: string;
}

export interface MarketingResponse {
  success: boolean;
  couponCode?: string;
  message?: string;
}

class MarketingApi {
  // Get marketing data (public)
  async getMarketingData(): Promise<MarketingData> {
    try {
      const response = await axios.get(`${API_URL}/api/marketing/active`);
      return response.data;
    } catch (error) {
      console.error("Error fetching marketing data:", error);
      throw error;
    }
  }

  // Submit marketing data (public)
  async submitMarketingData(
    data: MarketingSubmission
  ): Promise<MarketingResponse> {
    try {
      const response = await axios.post(
        `${API_URL}/api/marketing/submit`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting marketing data:", error);
      throw error;
    }
  }

  // Get all marketing data (admin only)
  async getAllMarketingData(): Promise<MarketingData[]> {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/marketing/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching all marketing data:", error);
      throw error;
    }
  }

  // Create marketing data (admin only)
  async createMarketingData(
    data: Partial<MarketingDataInput>
  ): Promise<MarketingData> {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      // Add text fields
      formData.append("title", data.title || "");
      formData.append("subtitle", data.subtitle || "");
      formData.append("description", data.description || "");
      formData.append(
        "discountPercentage",
        (data.discountPercentage || 5).toString()
      );
      formData.append("couponId", data.couponId || "");
      formData.append("isActive", (data.isActive || false).toString());

      // Add image file if provided
      if (data.image) {
        if (typeof data.image === "string") {
          formData.append("imageUrl", data.image);
        } else {
          formData.append("image", data.image as File);
        }
      }

      const response = await axios.post(
        `${API_URL}/api/marketing/admin`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating marketing data:", error);
      throw error;
    }
  }

  // Update marketing data (admin only)
  async updateMarketingData(
    id: string,
    data: Partial<MarketingDataInput>
  ): Promise<MarketingData> {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      // Add text fields
      if (data.title) formData.append("title", data.title);
      if (data.subtitle) formData.append("subtitle", data.subtitle);
      if (data.description) formData.append("description", data.description);
      if (data.discountPercentage !== undefined)
        formData.append(
          "discountPercentage",
          data.discountPercentage.toString()
        );
      if (data.couponId) formData.append("couponId", data.couponId);
      if (data.isActive !== undefined)
        formData.append("isActive", data.isActive.toString());

      // Add image file if provided
      if (data.image) {
        if (typeof data.image === "string") {
          formData.append("imageUrl", data.image);
        } else {
          formData.append("image", data.image as File);
        }
      }

      const response = await axios.put(
        `${API_URL}/api/marketing/admin/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating marketing data:", error);
      throw error;
    }
  }

  // Delete marketing data (admin only)
  async deleteMarketingData(id: string): Promise<void> {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/marketing/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error("Error deleting marketing data:", error);
      throw error;
    }
  }

  // Get marketing data by ID (admin only)
  async getMarketingDataById(id: string): Promise<MarketingData> {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/marketing/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching marketing data:", error);
      throw error;
    }
  }
}

const marketingApi = new MarketingApi();
export default marketingApi;
